import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import type {
  V2BookConnectionDescriptor,
  Bdkv2BookDescriptor,
} from '@/generated/hooks/bdk/models';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import i18n from '@/shared/i18n';
import {
  filterConnectionsByStage,
  isCreateSupersededBySharedConnection,
  type IFilteredConnection,
} from '../utils';

const ADD_NEW_CONNECTION_VALUE = '__add_new__';

const selectConnectionSchema = z.object({
  connectionId: z.string().min(
    1,
    i18n.t('integrations.connections.validation.required', {
      ns: 'integrations',
    }),
  ),
});

type SelectConnectionFormData = z.infer<typeof selectConnectionSchema>;

interface ISelectConnectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  integration?: Bdkv2BookDescriptor;
  connections: V2BookConnectionDescriptor[];
  onSelectConnection: (connection: IFilteredConnection) => void;
  onAddConnection: () => void;
  selectedConnection?: string;
}

export function SelectConnectionDialog({
  isOpen,
  onClose,
  integration,
  connections,
  onSelectConnection,
  onAddConnection,
  selectedConnection,
}: ISelectConnectionDialogProps) {
  const { t } = useTranslation('integrations');
  const { t: tCommon } = useTranslation('common');

  const form = useForm<SelectConnectionFormData>({
    resolver: zodResolver(selectConnectionSchema),
    defaultValues: {
      connectionId: '',
    },
  });

  // Reset form when dialog closes
  // eslint-disable-next-line no-restricted-syntax -- reason: Reset form when dialog closes to prevent stale state
  useEffect(() => {
    if (!isOpen) {
      const defaultConnectionId = selectedConnection || '';
      form.reset({ connectionId: defaultConnectionId });
    }
  }, [isOpen, form, selectedConnection]);

  if (!integration) {
    return null;
  }

  const connectionsDisplayData = filterConnectionsByStage(connections, 'draft');
  const selectedConnectionId = form.watch('connectionId');
  // Callers disagree on whether `connections` is scoped to this book: the
  // publish flow passes the whole workspace's. Narrow it here so a shared
  // connection on some unrelated book can't disable this one's "Add new".
  // (Only the gate is scoped — the list above keeps its existing behaviour.)
  const bookConnections = connections.filter(
    connection => connection.book_name === integration.name,
  );

  // "Add new" is disabled only when the book offers no authentication method
  // *and* a shared connection already covers it — then there is nothing left
  // to create. A book with auth methods stays open even when a shared
  // connection exists (e.g. HTTP, whose shared connection uses no auth).
  const canAddConnection = !isCreateSupersededBySharedConnection(
    integration,
    bookConnections,
  );

  /**
   * Handles form submission.
   * If "add new" is selected, calls onAddConnection.
   * Otherwise, finds the selected connection and calls onSelectConnection.
   */
  const handleSubmit = (data: SelectConnectionFormData) => {
    if (data.connectionId === ADD_NEW_CONNECTION_VALUE) {
      onAddConnection();
    } else {
      const selectedConnection = connectionsDisplayData.find(
        connection => connection.connection_id === data.connectionId,
      );
      if (selectedConnection) {
        onSelectConnection(selectedConnection);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('integrations.connections.configureTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('integrations.connections.configureDescription')}
          </DialogDescription>
        </DialogHeader>

        <Flex align="center" gap={2}>
          {integration.icon && (
            <img
              src={convertToDataUrl(integration.icon)}
              alt={integration.display_name}
              className="h-10 w-10 object-contain"
            />
          )}
          <Text level="base" weight="semibold">
            {integration.display_name}
          </Text>
        </Flex>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <Flex direction="column" gap={2} width="full">
              <Text level="small" color="muted" weight="medium">
                {t('integrations.connections.existingConnections')}
              </Text>
              <FormField
                control={form.control}
                name="connectionId"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-col gap-4"
                      >
                        {connectionsDisplayData.map(connection => {
                          const isAlreadySelected =
                            selectedConnection === connection.connection_id;
                          const connectionId = connection.connection_id;
                          return (
                            <Flex
                              direction="row"
                              align="center"
                              gap={3}
                              key={connectionId}
                            >
                              <RadioGroupItem
                                value={connectionId}
                                id={connectionId}
                              />
                              <FormLabel
                                htmlFor={connectionId}
                                aria-disabled={isAlreadySelected}
                                className="cursor-pointer"
                              >
                                <Flex direction="column" gap={0} width="full">
                                  <Flex
                                    justify="between"
                                    align="center"
                                    width="full"
                                  >
                                    <Flex align="center" gap={2}>
                                      <Text level="base" weight="medium">
                                        {connection.connection_name ||
                                          connectionId}
                                      </Text>
                                      {connection.book_version && (
                                        <Badge
                                          variant="secondary"
                                          className="px-2 py-0.5 text-xs"
                                        >
                                          v{connection.book_version}
                                        </Badge>
                                      )}
                                    </Flex>
                                  </Flex>
                                  {!connection.has_published_connection && (
                                    <Text level="small" color="muted">
                                      {t(
                                        'integrations.connections.requiresProductionConnection',
                                      )}
                                    </Text>
                                  )}
                                </Flex>
                              </FormLabel>
                            </Flex>
                          );
                        })}
                        <Separator />
                        <Tooltip>
                          {/* Tooltip hosted on the wrapping row: a disabled
                              radio doesn't emit the hover events a trigger
                              needs, and the row still does. */}
                          <TooltipTrigger asChild>
                            <Flex
                              direction="row"
                              align="center"
                              gap={3}
                              key={ADD_NEW_CONNECTION_VALUE}
                            >
                              <RadioGroupItem
                                value={ADD_NEW_CONNECTION_VALUE}
                                id={ADD_NEW_CONNECTION_VALUE}
                                disabled={!canAddConnection}
                              />
                              <FormLabel
                                htmlFor={ADD_NEW_CONNECTION_VALUE}
                                aria-disabled={!canAddConnection}
                                className={
                                  canAddConnection
                                    ? 'cursor-pointer'
                                    : 'cursor-not-allowed opacity-50'
                                }
                              >
                                <Flex direction="row" align="center" gap={2}>
                                  <Text level="base" weight="medium">
                                    {t('integrations.connections.addNew')}
                                  </Text>
                                </Flex>
                              </FormLabel>
                            </Flex>
                          </TooltipTrigger>
                          {!canAddConnection && (
                            <TooltipContent className="max-w-xs">
                              {t('integrations.connections.create.sharedOnly')}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Flex>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} type="button">
                {tCommon('common.action.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  !selectedConnectionId ||
                  (selectedConnectionId !== ADD_NEW_CONNECTION_VALUE &&
                    selectedConnectionId === selectedConnection)
                }
              >
                {tCommon('common.action.select')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default SelectConnectionDialog;
